<template>
  <div id="app" class="min-h-screen text-primary font-sans">
    <PageHeader />
    <main>
      <router-view></router-view>
    </main>

    <footer class="bg-primary text-white">
      <div class="max-w-page mx-auto px-6 py-16 md:py-20">
        <!-- Top: Brand + Links grid -->
        <div class="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-12">
          <!-- Brand column -->
          <div class="col-span-2 md:col-span-5">
            <img src="@/assets/logo.png" alt="Bloqin" class="block h-8 w-auto brightness-0 invert" />
            <p class="mt-5 text-stone/80 text-sm leading-relaxed max-w-sm">
              Gerçek dünya gayrimenkullerini blokzincir üzerinde tokenize ederek erişilebilir,
              şeffaf ve likit yatırımlar sunan teknoloji girişimi.
            </p>
            <div class="mt-6 flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/bloqin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bloqin LinkedIn"
                class="w-10 h-10 rounded-full border border-stone/30 flex items-center justify-center text-stone/80 hover:border-secondary hover:text-secondary hover:bg-secondary/5 transition-colors"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.852 3.37-1.852 3.601 0 4.267 2.37 4.267 5.455v6.288zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.222 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0z" />
                </svg>
              </a>
            </div>
          </div>

          <!-- Platform column -->
          <div class="md:col-span-2 md:col-start-7">
            <h4 class="text-xs font-medium tracking-label uppercase text-secondary">Platform</h4>
            <ul class="mt-5 space-y-3 text-sm text-stone/80">
              <li><router-link to="/#features" class="hover:text-secondary transition-colors">Yatırımlar</router-link></li>
              <li><router-link to="/#how" class="hover:text-secondary transition-colors">Nasıl Çalışır</router-link></li>
              <li><router-link to="/#roadmap" class="hover:text-secondary transition-colors">Yol Haritası</router-link></li>
              <li><router-link to="/#waitlist" class="hover:text-secondary transition-colors">Bekleme Listesi</router-link></li>
            </ul>
          </div>

          <!-- Company column -->
          <div class="md:col-span-2">
            <h4 class="text-xs font-medium tracking-label uppercase text-secondary">Şirket</h4>
            <ul class="mt-5 space-y-3 text-sm text-stone/80">
              <li><router-link to="/about" class="hover:text-secondary transition-colors">Hakkımızda</router-link></li>
              <li><router-link to="/apply" class="hover:text-secondary transition-colors">Birlikte Çalışalım</router-link></li>
              <li><router-link to="/login" class="hover:text-secondary transition-colors">Yatırımcı Paneli</router-link></li>
            </ul>
          </div>

          <!-- Legal column -->
          <div class="md:col-span-3">
            <h4 class="text-xs font-medium tracking-label uppercase text-secondary">Yasal</h4>
            <ul class="mt-5 space-y-3 text-sm text-stone/80">
              <li><a href="#" class="hover:text-secondary transition-colors">Gizlilik Politikası</a></li>
              <li><a href="#" class="hover:text-secondary transition-colors">Çerez Politikası</a></li>
              <li><a href="#" class="hover:text-secondary transition-colors">KVKK Aydınlatma Metni</a></li>
              <li><a href="#" class="hover:text-secondary transition-colors">Kullanım Şartları</a></li>
            </ul>
          </div>
        </div>

        <!-- Disclaimer (collapsible) -->
        <details class="mt-14 md:mt-16 pt-8 border-t border-stone/10 group">
          <summary class="cursor-pointer list-none flex items-center justify-between gap-4 text-xs font-medium tracking-label uppercase text-stone/60 hover:text-secondary transition-colors">
            <span>Yasal Uyarı</span>
            <span class="w-7 h-7 rounded-full border border-stone/30 flex items-center justify-center text-base leading-none group-open:rotate-45 transition-transform">
              +
            </span>
          </summary>
          <div class="mt-5 space-y-3 text-stone/60 text-[13px] leading-relaxed max-w-4xl">
            <p>
              Bu web sitesinde yer alan bilgiler sadece genel bilgilendirme amaçlıdır ve yatırım tavsiyesi niteliğinde
              değildir. Bloqin, kullanıcılarına tokenize edilmiş gerçek dünya varlıklarına yatırım yapma fırsatı sunar ancak
              tüm yatırımlar risk içerir ve geçmiş performans gelecekteki sonuçların garantisi değildir.
            </p>
            <p>
              Kullanıcılar, Bloqin platformu üzerinden yatırım yapmadan önce kendi araştırmalarını yapmalı ve gerektiğinde
              profesyonel bir finansal danışmana danışmalıdır. Bloqin, kullanıcıların yatırım kararlarından veya platform
              üzerinden yapılan işlemlerden doğabilecek kayıplardan sorumlu değildir.
            </p>
            <p>
              Platformumuzun sunduğu hizmetler, bulunduğunuz ülkenin finansal düzenlemelerine tabi olabilir. Kullanıcılar,
              ilgili yasal düzenlemelere uygun hareket etmekten kendileri sorumludur.
            </p>
          </div>
        </details>

        <!-- Bottom row -->
        <div class="mt-10 pt-8 border-t border-stone/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-stone/60">
          <p>
            Bloqin bir
            <a
              href="https://ervteknoloji.com"
              target="_blank"
              rel="noopener noreferrer"
              class="text-secondary font-medium hover:underline"
            >
              ERV Teknoloji A.Ş.
            </a>
            girişimidir.
          </p>
          <p>© 2026 Bloqin · Tüm hakları saklıdır.</p>
        </div>
      </div>
    </footer>

    <CookieBar />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import PageHeader from './components/PageHeader.vue';
import CookieBar from './components/CookieBar.vue';

export default defineComponent({
  components: {
    PageHeader,
    CookieBar,
  },
});
</script>

<style>
html, body {
  background-color: #ffffff;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  background-image:
    radial-gradient(ellipse 1400px 700px at 50% -100px, rgba(0, 221, 179, 0.14), rgba(0, 221, 179, 0) 60%),
    radial-gradient(ellipse 900px 600px at 100% 30%, rgba(0, 221, 179, 0.06), rgba(0, 221, 179, 0) 70%);
  background-attachment: fixed;
  background-repeat: no-repeat;
}

#app {
  font-family: Jost, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0A253A;
}
</style>
